import {
  IconAdd,
  IconChannelSelective,
  IconIdentifier,
  IconTrash,
} from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import Input from '@/ui/Input/Input';
import CustomSelect from '@/ui/Select/Select';
import { T } from '@/ui/Text/Text';
import { DURATIONS, TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { createId } from '@paralleldrive/cuid2';
import React from 'react';
import { useIntl } from 'react-intl';
import { GeneralSettingsType } from '../../SettingsSidebar/types';
import { StyledGridContainer } from '../styles';

interface Props {
  buttons: GeneralSettingsType['buttons'];
  setButtons: (newValue: GeneralSettingsType['buttons']) => void;
  moreButtonsAllowed: boolean;
}

function ButtonHardwareSettings({
  buttons,
  setButtons,
  moreButtonsAllowed,
}: Props) {
  const intl = useIntl();
  const toast = useToast();

  // --- Button Identifier Options ---
  const buttonIdentifierOptions = React.useMemo(
    () => [
      { value: 'A', label: 'A' },
      { value: 'B', label: 'B' },
      { value: 'C', label: 'C' },
      { value: 'D', label: 'D' },
    ],
    [],
  );

  // --- Utility Functions ---

  const updateButton = (
    index: number,
    key: 'channel' | 'buttonIdentifier' | 'name',
    value: string | null,
  ) => {
    const newbuttons = [...buttons];
    newbuttons[index] = {
      ...newbuttons[index],
      [key]: value,
    };
    setButtons(newbuttons);
  };

  const deleteButton = (index: number) => {
    const newbuttons = buttons.filter((_, i) => i !== index);
    setButtons(newbuttons);
  };

  const addButton = () => {
    if (!moreButtonsAllowed) {
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'No available Buttons',
          id: 'jaH6+6',
        }),
        subtitle: intl.formatMessage({
          defaultMessage:
            'To add more Buttons, please register additional controllers.',
          id: 'UVEXZx',
        }),
        time: DURATIONS.Medium,
      });
      return;
    }
    setButtons([
      ...buttons,
      {
        id: createId(),
        channel: null,
        buttonIdentifier: 'A',
        name: null,
      },
    ]);
  };

  // --- JSX ---

  return (
    <Flex direction="column" gap={16}>
      <T h4>
        {intl.formatMessage({
          defaultMessage: 'Registered Buttons',
          id: 'TFTv9t',
        })}
      </T>

      {buttons.length !== 0 && (
        <StyledGridContainer columns={3}>
          <>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Channel',
                id: 'KeO51o',
              })}
            </T>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'ID', id: 'qlcuNQ' })}
            </T>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' })}
            </T>
            <div />
          </>

          {buttons.map((button, index) => {
            const hasAllValues = Boolean(
              button.channel && button.buttonIdentifier && button.name,
            );
            return (
              <React.Fragment key={button.id}>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={button.channel || ''}
                    onChange={(e) => updateButton(index, 'channel', String(e))}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Channel',
                      id: 'KeO51o',
                    })}
                    icon={<IconChannelSelective />}
                  />
                </div>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <CustomSelect
                    required
                    options={buttonIdentifierOptions}
                    value={
                      button.buttonIdentifier
                        ? {
                            value: button.buttonIdentifier,
                            label: button.buttonIdentifier,
                          }
                        : null
                    }
                    onChange={(e) => {
                      updateButton(
                        index,
                        'buttonIdentifier',
                        e && !Array.isArray(e) ? e.value : null,
                      );
                    }}
                    size="small"
                    isSearchable={false}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'ID',
                      id: 'qlcuNQ',
                    })}
                    icon={<IconIdentifier />}
                  />
                </div>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={button.name || ''}
                    onChange={(e) => updateButton(index, 'name', String(e))}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Name',
                      id: 'HAlOn1',
                    })}
                  />
                </div>
                <Button
                  size="minimum"
                  mode="transparent"
                  onClick={() => deleteButton(index)}
                >
                  <IconTrash />
                </Button>
              </React.Fragment>
            );
          })}
        </StyledGridContainer>
      )}

      <Button mode="transparent" onClick={addButton}>
        <IconAdd />
      </Button>
    </Flex>
  );
}

export default ButtonHardwareSettings;
